import { forwardRef, useId, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from 'react'

import { cn } from '@/lib/utils'

interface FieldShellProps {
  label?: string
  hint?: ReactNode
  error?: string | null
  required?: boolean
  htmlFor?: string
  children: ReactNode
  className?: string
}

export function FieldShell({
  label,
  hint,
  error,
  required,
  htmlFor,
  children,
  className,
}: FieldShellProps) {
  return (
    <div className={cn('w-full', className)}>
      {label && (
        <label htmlFor={htmlFor} className="mb-2 block text-[13px] font-medium text-ink-soft">
          {label}
          {required && <span className="ml-1 text-danger">*</span>}
        </label>
      )}
      {children}
      {error ? (
        <p id={htmlFor ? `${htmlFor}-error` : undefined} role="alert" className="mt-1.5 text-[13px] text-danger">
          {error}
        </p>
      ) : (
        hint && <p id={htmlFor ? `${htmlFor}-hint` : undefined} className="mt-1.5 text-[13px] text-ink-muted">{hint}</p>
      )}
    </div>
  )
}

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string
  hint?: ReactNode
  error?: string | null
  /** `underline` is the wizard style; `box` is used in dashboard forms. */
  appearance?: 'underline' | 'box'
  trailing?: ReactNode
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, hint, error, appearance = 'underline', trailing, className, id, required, ...props },
  ref,
) {
  const generatedId = useId()
  const fieldId = id ?? generatedId

  return (
    <FieldShell
      label={label}
      hint={hint}
      error={error}
      required={required}
      htmlFor={fieldId}
    >
      <div className="relative">
        <input
          ref={ref}
          id={fieldId}
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${fieldId}-error` : hint ? `${fieldId}-hint` : undefined}
          className={cn(
            appearance === 'underline' ? 'field-underline' : 'field-box',
            error && (appearance === 'underline' ? 'border-danger' : 'border-danger'),
            trailing && 'pr-10',
            className,
          )}
          {...props}
        />
        {trailing && (
          <div className="absolute right-0 top-1/2 flex -translate-y-1/2 items-center gap-2">
            {trailing}
          </div>
        )}
      </div>
    </FieldShell>
  )
})

export interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string
  hint?: ReactNode
  error?: string | null
  appearance?: 'underline' | 'box'
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  { label, hint, error, appearance = 'underline', className, id, required, ...props },
  ref,
) {
  const generatedId = useId()
  const fieldId = id ?? generatedId

  return (
    <FieldShell label={label} hint={hint} error={error} required={required} htmlFor={fieldId}>
      <textarea
        ref={ref}
        id={fieldId}
        required={required}
        aria-describedby={error ? `${fieldId}-error` : hint ? `${fieldId}-hint` : undefined}
        aria-invalid={error ? true : undefined}
        className={cn(
          appearance === 'underline' ? 'field-underline resize-y' : 'field-box resize-y',
          error && 'border-danger',
          className,
        )}
        {...props}
      />
    </FieldShell>
  )
})
